// -----------------------------------------------------------------------------
// CompletedTreatmentHub
//
// The COMPLETED-TREATMENT "wrap-up hub". Replaces the old confirm-only
// CustomCompleteTreatmentDialog while staying 100% backward-compatible:
//
//   • row PRESENT  → rich wrap-up experience:
//                      TreatmentContextHeader  (treatment context, read-only)
//                    + CompletionChecklist     (open TREATMENT_STARTED items)
//                    + ActionPanel             (shown when a checklist item is clicked)
//                    + footer: Cancel + "Complete treatment"
//   • row ABSENT   → legacy warning-box + Cancel/Finish layout, so the 4 callers
//                    that pass no row keep working unchanged.
//
// Reuses the original full-screen Dialog + Slide + sticky AppBar + fixed
// bottom-bar scaffold. The completion action (handleYes) and its API body are
// unchanged — this component never touches the CompleteTreatment thunk.
//
// Row shape: the TREATMENT_STARTED grid rows are produced by parseV2ToV1, which
// spreads lead fields to the top level and nests the treatment under
// row.lead_treatments[0] (treatment_data = the v2 treatment object). leadId and
// a minimal leadDetail are reconstructed here so the checklist hook + panels
// resolve correctly. See the treatment_data investigation note in the PR.
// -----------------------------------------------------------------------------
import React, { forwardRef, useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { AppBar, Box, Button, Container, Dialog, DialogContent, DialogActions, LinearProgress, Slide, Typography, useMediaQuery } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/Iconify';
import { dispatch, useSelector } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { completeChecklistItem } from 'src/redux/slices/taskSlice';
import { updateError } from 'src/redux/slices/user';
import { followUpSlice } from 'src/redux/slices/lead';
import cascadeTaskDueDates from 'src/utils/cascadeTaskDueDates';
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import ActionPanel from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/ActionPanel';
import {
    commonDialogHeaderStyle,
    reminderTitleStyle,
    cancelBoxStyle,
    reminderDialogStyle,
} from 'src/styles/ScheduleReminder';
import CompletionChecklist from './CompletionChecklist';
import useCompletionChecklist from './useCompletionChecklist';
import HubSidebar from './HubSidebar';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

// Resolve the numeric lead id from a (possibly composite) flat-row id.
function resolveLeadId(row) {
    if (!row) return null;
    if (row.lead_id != null && row.lead_id !== 0) return row.lead_id;
    // parseV2ToV1 rows have id = `${leadId}-${index}`.
    const idStr = String(row.id ?? '');
    const leadPart = idStr.split('-')[0];
    return leadPart ? Number(leadPart) : null;
}

// Resolve the genuine lead-treatment object for the row the hub was opened on.
//
// PROBED on production (2026-05-28) against Carina (p18 lead 115648) and Raheem
// (p92 lead 153441 — a 2-treatment lead):
//   • V2-flat row → parseV2ToV1() → useLeadTableHelper.handleTableData().
//   • The REAL lead_treatment object lives at `row.treatmentData[0]`
//     (handleTableData sets `treatmentData: item.lead_treatments`, and the
//      parseV2ToV1 lead_treatments[0] is the spread v2 treatment object).
//   • Confirmed field paths on that object:
//       row.treatmentData[0].id                              → real lead_treatment id
//                                                              (Carina 113395, Raheem 135917/135918)
//       row.treatmentData[0].treatment_data.treatment_item.id → real treatment_item id
//                                                              (Carina 22, Raheem 48/22)
//       row.treatmentData[0].status                          → treatment status
//   • row.id is the MANGLED "${leadId}-${index}" composite — NEVER the lead_treatment id.
//
// The TreatmentStarted table is one row per treatment, so treatmentData[0] is
// THIS row's treatment. We therefore build a single-element lead_treatments[]
// from it (not a status search) so the hook scopes to exactly this treatment —
// critical for multi-treatment leads (Raheem's Invisalign checklist only
// resolves when the hub is opened on the Invisalign row).
function resolveRowTreatment(row) {
    if (!row) return null;
    // Prefer the helper-transformed table shape, then the parsed flat shape,
    // then the raw v2-flat shape. Each carries the genuine ids.
    const tx =
        row.treatmentData?.[0] ??       // useLeadTableHelper rows
        row.lead_treatments?.[0] ??     // parseV2ToV1 rows (pre-helper)
        null;
    if (tx) {
        // treatment_data is where treatment_item.id lives; on raw rows it may be
        // under `treatment` instead.
        const treatmentData = tx.treatment_data ?? tx.treatment ?? null;
        const name =
            treatmentData?.treatment_item?.name ??
            (typeof tx.treatment === 'string' ? tx.treatment : '') ??
            '';
        return {
            id: tx.id ?? null,                 // genuine lead_treatment id
            status: tx.status ?? row.status ?? null,
            treatment_data: treatmentData,     // carries treatment_item.id
            treatment: name,
        };
    }
    // Raw v2-flat fallback (treatment is an object with treatment_item).
    const treatmentData = row.treatment_data ?? row.treatment ?? null;
    return {
        id: row.treatment_id ?? null,          // helper exposes the real id here too
        status: row.status ?? null,
        treatment_data: treatmentData,
        treatment: treatmentData?.treatment_item?.name
            ?? (typeof row.treatment === 'string' ? row.treatment : '') ?? '',
    };
}

// Reconstruct a minimal leadDetail object for the checklist hook + ActionPanel.
// lead_treatments[] drives the treatment filter in useCompletionChecklist (needs
// .id = real lead_treatment id, .treatment_data.treatment_item.id = real
// treatment_item id, .status) and the panels' treatmentId (lead_treatments[0].id).
function buildLeadDetail(row, leadId, treatmentOverride) {
    if (!row) return null;
    const lead = row.lead || row; // raw rows nest lead; parsed rows spread it.
    const treatment = treatmentOverride || resolveRowTreatment(row);
    return {
        id: leadId,
        first_name: lead?.first_name ?? row.first_name ?? '',
        last_name: lead?.last_name ?? row.last_name ?? '',
        email: lead?.lead_contact?.email ?? row.email ?? '',
        lead_contact: lead?.lead_contact ?? row.lead_contact ?? null,
        lead_treatments: treatment ? [treatment] : [],
    };
}

// A genuine lead_treatment id is a positive integer. The mangled row.id
// composite ("115648-0-0") is not — use this to decide if a supplemental GET
// is needed to recover the real ids.
function hasCleanTreatmentIds(treatment) {
    if (!treatment) return false;
    const ltId = treatment.id;
    const tiId = treatment.treatment_data?.treatment_item?.id;
    const isCleanInt = (v) => v != null && Number.isInteger(Number(v)) && !String(v).includes('-');
    return isCleanInt(ltId) && isCleanInt(tiId);
}

const CompletedTreatmentHub = ({ isLoader, open, handleClose, handleYes, row, practiceId }) => {
    const [actionPanelItem, setActionPanelItem] = useState(null);
    // Populated only by the supplemental-GET fallback when the row lacks clean ids.
    const [fetchedTreatment, setFetchedTreatment] = useState(null);
    // Pause dialog state — mirrors AttendedGuidedChecklistV2 (date-picker for paused_until).
    const [pauseDialogItem, setPauseDialogItem] = useState(null);
    const [pauseUntilDate, setPauseUntilDate] = useState('');
    // Sidebar split-view state. 'details' is the resting tab; email/whatsapp are
    // opened programmatically by the shared ActionPanel / FollowUpPanel via
    // onOpenSidebarTab. sidebarPulse re-keys the attention ring on each open.
    const [sidebarTab, setSidebarTab] = useState('details');
    const [sidebarPulse, setSidebarPulse] = useState(0);

    // Stack to a single column on narrow viewports — sidebar drops BELOW the main
    // column (it is never hidden; it hosts the composers).
    const isNarrow = useMediaQuery('(max-width: 900px)');

    const leadId = useMemo(() => resolveLeadId(row), [row]);
    // The treatment resolved straight off the row (the cheap, preferred path).
    const rowTreatment = useMemo(() => resolveRowTreatment(row), [row]);
    // followLead primes the WhatsApp composer (state.lead.followLead), exactly as
    // OpenConsultationPopup does. Read it for the sidebar.
    const followLead = useSelector((s) => s.lead.followLead);
    const leadDetail = useMemo(
        () => buildLeadDetail(row, leadId, fetchedTreatment || rowTreatment),
        [row, leadId, fetchedTreatment, rowTreatment]
    );

    const checklistEnabled = !!row && hasFeatureAccess('tasks-v2', practiceId);

    // Supplemental fallback: if the row did NOT carry clean lead_treatment /
    // treatment_item ids, fetch the lead's treatments and pick the one matching
    // this row (by status, preferring TREATMENT_STARTED). Probing showed the
    // table rows DO carry clean ids, so this is a safety net, not the norm.
    useEffect(() => {
        setFetchedTreatment(null);
        if (!open || !checklistEnabled || !practiceId || !leadId) return undefined;
        if (hasCleanTreatmentIds(rowTreatment)) return undefined;

        let cancelled = false;
        (async () => {
            try {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/`
                );
                let list = [];
                if (Array.isArray(res?.data)) list = res.data;
                else if (Array.isArray(res?.data?.results)) list = res.data.results;
                if (cancelled || list.length === 0) return;
                const active = list.find((t) => t.status === 'TREATMENT_STARTED') || list[0];
                if (!active) return;
                setFetchedTreatment({
                    id: active.id ?? null,
                    status: active.status ?? null,
                    treatment_data: active.treatment_data ?? active.treatment ?? null,
                    treatment: (active.treatment_data ?? active.treatment)?.treatment_item?.name ?? '',
                });
            } catch (err) {
                Sentry.captureException(err, { tags: { context: 'completion_hub_treatment_fallback' } });
            }
        })();
        return () => { cancelled = true; };
    }, [open, checklistEnabled, practiceId, leadId, rowTreatment]);

    // Prime state.lead.followLead (used by FollowUpWhatsappChat) on open — same
    // call OpenConsultationPopup makes. Reset sidebar to the details tab on open.
    useEffect(() => {
        if (!open || !leadId || !practiceId) return;
        setSidebarTab('details');
        dispatch(followUpSlice(leadId, practiceId, 'LeadDetail'));
    }, [open, leadId, practiceId]);

    // Programmatic sidebar switch — passed to ActionPanel/FollowUpPanel. Switching
    // to email/whatsapp replays the attention pulse so the composer draws the eye.
    const onOpenSidebarTab = useCallback((v) => {
        setSidebarTab(v);
        setSidebarPulse((n) => n + 1);
    }, []);

    const handleSidebarBack = useCallback(() => setSidebarTab('details'), []);

    // Own the checklist hook at the hub level so we can refresh() after a panel
    // completion or an undo — mirrors AttendedGuidedChecklist, which calls the
    // hook's refresh() after every complete/undo so the list reconciles in place.
    // groups/loading/templatesEmpty are passed DOWN to CompletionChecklist as
    // props (it no longer calls the hook itself).
    const {
        initialLoading: checklistLoading,
        templatesEmpty: checklistEmpty,
        groups: checklistGroups,
        refresh: refreshChecklist,
    } = useCompletionChecklist({ practiceId, leadId, leadDetail });

    const handleItemClick = useCallback((item) => {
        if (item?._preview) return; // preview items are non-interactive
        setActionPanelItem(item);
    }, []);

    // Panel CANCEL (no completion): clear the panel and refresh (harmless).
    const handlePanelClose = useCallback(() => {
        setActionPanelItem(null);
        setSidebarTab('details');
        if (checklistEnabled) refreshChecklist({ bustCache: true });
    }, [checklistEnabled, refreshChecklist]);

    // Panel COMPLETE: a panel's own action button (e.g. "Save", "Send link",
    // "Mark follow-up done") calls onComplete(item). The hub must actually mark
    // the checklist item done on the BE — otherwise the panel looks like a dead
    // box (it accepts input but nothing persists / the item never moves to Done).
    // Mirrors AttendedGuidedChecklistV2.handlePanelComplete: POST /complete/ with
    // the canonical body, forwarding resolved_action when a panel attached it
    // (LEAD_NEXT_ACTION / OPEN_FOLLOW_UP require it). Then close + cache-bust.
    const handlePanelComplete = useCallback((completedItem) => {
        setActionPanelItem(null);
        setSidebarTab('details');
        const taskId = completedItem?.taskId ?? completedItem?.rawTask?.id;
        const itemId = completedItem?.itemId ?? completedItem?.id;
        if (!checklistEnabled || !practiceId || !taskId || !itemId) {
            if (checklistEnabled) refreshChecklist({ bustCache: true });
            return;
        }
        const payload = completedItem?.resolvedAction
            ? { resolved_action: completedItem.resolvedAction }
            : null;
        dispatch(completeChecklistItem(
            practiceId, taskId, itemId,
            () => { refreshChecklist({ bustCache: true }); },
            payload,
        ));
    }, [checklistEnabled, practiceId, refreshChecklist]);

    // Reopen a completed/skipped/paused checklist item (mirrors the attended grid),
    // then refresh so the item moves back out of Done in place.
    const handleUndo = useCallback(async (item) => {
        const taskId = item?.taskId ?? item?.rawTask?.id;
        const itemId = item?.itemId ?? item?.id;
        if (!practiceId || !taskId || !itemId) return;
        try {
            await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/reopen/`,
                'POST'
            );
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'completion_hub_undo' } });
        } finally {
            if (checklistEnabled) refreshChecklist({ bustCache: true });
        }
    }, [practiceId, checklistEnabled, refreshChecklist]);

    // SKIP — mirrors AttendedGuidedChecklistV2.handleSkip: same /complete/ thunk
    // with CHECKLIST_COMPLETION_STATE.SKIPPED. Then cache-bust refresh.
    const handleSkip = useCallback((skippedItem) => {
        const taskId = skippedItem?.taskId ?? skippedItem?.rawTask?.id;
        const itemId = skippedItem?.itemId ?? skippedItem?.id;
        if (!checklistEnabled || !practiceId || !taskId || !itemId) return;
        dispatch(completeChecklistItem(
            practiceId, taskId, itemId,
            ({ success }) => { if (success) refreshChecklist({ bustCache: true }); },
            { source: 'completion_hub_skip' },
            CHECKLIST_COMPLETION_STATE.SKIPPED,
        ));
    }, [checklistEnabled, practiceId, refreshChecklist]);

    // PAUSE (CHECKLIST-WIDE) — the /pause/ endpoint pauses the WHOLE task-group,
    // not just one item (confirmed at runtime), so the hub surfaces it as a single
    // checklist-level "Pause checklist" control rather than a per-item action.
    // Opens a dialog pre-filled to 7 days from now at 09:00 (local) and uses any
    // active item as the handle into its task-group. Same endpoint as attended.
    const handlePauseChecklist = useCallback(() => {
        const { today = [], later = [] } = checklistGroups || {};
        const activeItem = [...today, ...later].find((i) => i?.taskId && i?.itemId)
            || [...today, ...later][0]
            || null;
        if (!activeItem) return;
        const d = new Date();
        d.setDate(d.getDate() + 7);
        const pad = (n) => String(n).padStart(2, '0');
        const localStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
        setPauseUntilDate(localStr);
        setPauseDialogItem(activeItem);
    }, [checklistGroups]);

    const handlePauseConfirm = useCallback(async () => {
        const item = pauseDialogItem;
        const taskId = item?.taskId ?? item?.rawTask?.id;
        const itemId = item?.itemId ?? item?.id;
        if (!taskId || !itemId || !pauseUntilDate) return;
        setPauseDialogItem(null);
        // Same endpoint + body as AttendedGuidedChecklistV2.handlePauseConfirm —
        // pauses the whole checklist/task-group until the chosen date.
        const resp = await callApi(
            `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/pause/`,
            'post',
            { paused_until: new Date(pauseUntilDate).toISOString() }
        );
        if (resp.ok) {
            dispatch(updateError({ success: true, message: 'Checklist paused' }));
            refreshChecklist({ bustCache: true });
        } else {
            dispatch(updateError({ success: false, message: resp?.data?.detail || 'Failed to pause checklist' }));
        }
    }, [pauseDialogItem, pauseUntilDate, practiceId, refreshChecklist]);

    // CHANGE DUE DATE — mirrors AttendedGuidedChecklistV2.handleChangeDueDate:
    // cascade the delta to all incomplete dated tasks, PATCH each task's due_at,
    // then refresh. (Preview items have no taskId — spawn from template first.)
    const handleChangeDueDate = useCallback(async (item, newDate) => {
        if (!item?.taskId) {
            if (!item?._templateId) {
                dispatch(updateError({ success: false, message: "Due dates can't be set on this item yet." }));
                return;
            }
            const spawnResp = await callApi(
                `/api/v1/practices/${practiceId}/tasks/from-template/`,
                'post',
                { template_id: item._templateId, lead_id: leadId }
            );
            if (!spawnResp.ok && spawnResp.status !== 200 && spawnResp.status !== 201) {
                dispatch(updateError({ success: false, message: "Couldn't create tasks. Please try again." }));
                return;
            }
            const newTaskId = spawnResp.data?.id;
            if (newTaskId) {
                await callApi(`/api/v1/practices/${practiceId}/tasks/${newTaskId}/`, 'patch', { due_at: newDate.toISOString() });
                dispatch(updateError({ success: true, message: 'Tasks created and due date set.' }));
            } else {
                dispatch(updateError({ success: true, message: 'Tasks created! Select a task to set its due date.' }));
            }
            refreshChecklist({ bustCache: true });
            return;
        }
        const { today = [], later = [], done = [] } = checklistGroups || {};
        const allItems = [...today, ...later, ...done];
        const cascade = cascadeTaskDueDates({
            items: allItems,
            anchorTaskId: item.taskId,
            oldDueAt: item.due_at,
            newDueAt: newDate,
        });

        const results = await Promise.allSettled(
            cascade.map(({ taskId, newDueAt: d }) =>
                callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', { due_at: d.toISOString() })
            )
        );

        const anyFailed = results.some((r) => r.status === 'rejected' || !r.value?.ok);
        if (anyFailed) {
            dispatch(updateError({ success: false, message: "Couldn't update due dates. Please try again." }));
            return;
        }

        const nShifted = cascade.length;
        const oldMs = item.due_at ? new Date(item.due_at).getTime() : Date.now();
        const deltaDays = Math.round(Math.abs(newDate.getTime() - oldMs) / (24 * 60 * 60 * 1000));
        dispatch(updateError({
            success: true,
            message: `Due dates updated — ${nShifted} task${nShifted !== 1 ? 's' : ''} shifted by ${deltaDays} day${deltaDays !== 1 ? 's' : ''}`,
        }));
        refreshChecklist({ bustCache: true });
    }, [practiceId, leadId, checklistGroups, refreshChecklist]);

    // Progress summary for the main column header (only when the checklist is
    // enabled, loaded and non-empty). done / (today+later+done).
    const { today: gToday = [], later: gLater = [], done: gDone = [] } = checklistGroups || {};
    const progressTotal = gToday.length + gLater.length + gDone.length;
    const progressDone = gDone.length;
    const showProgress = checklistEnabled && !checklistLoading && !checklistEmpty && progressTotal > 0;
    const progressPct = progressTotal > 0 ? Math.round((progressDone / progressTotal) * 100) : 0;

    return (
        <>
        <Dialog
            fullScreen
            TransitionComponent={Transition}
            open={open}
            onClose={handleClose}
            sx={[reminderDialogStyle(), { pt: { xs: 0, sm: 0 }, '.MuiDialog-paperFullScreen': { bgcolor: '#F5F5F7', scrollbarGutter: 'stable' } }]}
        >
            <AppBar sx={[commonDialogHeaderStyle, { position: 'sticky !important', top: 0, alignItems: 'center' }]}>
                <Typography sx={reminderTitleStyle} variant="h6" component="div">
                    {row ? 'Treatment in progress' : 'Complete treatment'}
                </Typography>
                <Box sx={cancelBoxStyle}>
                    <Button
                        color="inherit"
                        variant="outlined"
                        style={{ borderColor: '#FFFFFF', color: '#fff' }}
                        onClick={handleClose}
                        disabled={isLoader}
                    >
                        Cancel
                    </Button>
                </Box>
            </AppBar>

            {row ? (
                // 70/30 split view. Height fills the dialog below the 64px AppBar;
                // each column scrolls independently. Stacks to a column < 900px.
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isNarrow ? 'column' : 'row',
                        height: isNarrow ? 'auto' : 'calc(100vh - 64px)',
                        overflow: isNarrow ? 'visible' : 'hidden',
                    }}
                >
                    {/* MAIN ~70% */}
                    <Box
                        sx={{
                            flex: isNarrow ? '1 1 auto' : '0 0 70%',
                            minWidth: 0,
                            overflowY: isNarrow ? 'visible' : 'auto',
                            p: 3,
                            pb: 12,
                        }}
                    >
                        {showProgress && (
                            <Box sx={{ mb: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.01em' }}>
                                        Checklist progress
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#6e6e73', letterSpacing: '-0.005em' }}>
                                        {progressDone} of {progressTotal} done
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressPct}
                                    sx={{
                                        height: 8,
                                        borderRadius: 999,
                                        bgcolor: 'rgba(0,0,0,0.06)',
                                        '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: '#00AB55' },
                                    }}
                                />
                            </Box>
                        )}

                        {checklistEnabled && (
                            <CompletionChecklist
                                initialLoading={checklistLoading}
                                templatesEmpty={checklistEmpty}
                                groups={checklistGroups}
                                onItemClick={handleItemClick}
                                onUndo={handleUndo}
                                onSkip={handleSkip}
                                onChangeDueDate={handleChangeDueDate}
                                onPauseChecklist={handlePauseChecklist}
                            />
                        )}

                        {/* NON-email ActionPanel renders here in the main column.
                            Email/WhatsApp actions switch the sidebar instead (via
                            onOpenSidebarTab) — the shared ActionPanel handles that. */}
                        {actionPanelItem && (
                            <Box
                                sx={{
                                    mt: 2.5,
                                    bgcolor: '#ffffff',
                                    borderRadius: '14px',
                                    overflow: 'hidden',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                    position: 'relative',
                                    minHeight: 280,
                                }}
                            >
                                <ActionPanel
                                    item={actionPanelItem}
                                    practiceId={practiceId}
                                    leadId={leadId}
                                    leadDetail={leadDetail}
                                    onClose={handlePanelClose}
                                    onComplete={handlePanelComplete}
                                    onOpenSidebarTab={onOpenSidebarTab}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* SIDEBAR ~30% */}
                    <Box
                        sx={{
                            flex: isNarrow ? '1 1 auto' : '0 0 30%',
                            minWidth: isNarrow ? 0 : 280,
                            overflowY: isNarrow ? 'visible' : 'auto',
                            borderLeft: isNarrow ? 'none' : '1px solid rgba(0,0,0,0.08)',
                            borderTop: isNarrow ? '1px solid rgba(0,0,0,0.08)' : 'none',
                            bgcolor: '#fff',
                            position: 'relative',
                            pb: 12,
                        }}
                    >
                        <HubSidebar
                            sidebarTab={sidebarTab}
                            sidebarPulse={sidebarPulse}
                            onBack={handleSidebarBack}
                            row={row}
                            leadId={leadId}
                            practiceId={practiceId}
                            leadDetail={leadDetail}
                            followLead={followLead}
                            handleClose={handleClose}
                            treatmentId={rowTreatment?.id}
                            treatmentStatus={rowTreatment?.status}
                        />
                    </Box>
                </Box>
            ) : (
                <Container maxWidth="md" sx={{ py: 4, pb: 12 }}>
                    <Box sx={{ p: 4, bgcolor: '#ffffff', borderRadius: '12px', width: '100%' }}>
                        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', mb: 0.5 }}>
                            Mark treatment as complete?
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mb: 3 }}>
                            The treatment will be closed and moved to the completed state.
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            gap: 1.25,
                            p: 2,
                            bgcolor: 'rgba(255, 149, 0, 0.08)',
                            borderLeft: '3px solid #ff9500',
                            borderRadius: '8px',
                        }}>
                            <Iconify icon={'bxs:info-circle'} width={18} height={18} sx={{ color: '#ff9500', mt: 0.125, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '13px', color: '#1d1d1f', letterSpacing: '-0.01em', lineHeight: 1.45 }}>
                                This action cannot be undone. Once completed, the treatment is locked from further edits.
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            )}

            <Box
                sx={{
                    width: '100%',
                    position: 'fixed',
                    left: 0,
                    bottom: 0,
                    pb: 1.5,
                    pt: 1.5,
                    bgcolor: 'rgba(246, 246, 248, 0.82)',
                    backdropFilter: 'saturate(180%) blur(20px)',
                    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                    borderTop: '0.5px solid rgba(0, 0, 0, 0.10)',
                    zIndex: 99,
                }}
            >
                {row ? (
                    // Hub footer — "Done" is the PRIMARY everyday exit (closes the
                    // popup, NO status change — treatment stays TREATMENT_STARTED).
                    // "Complete treatment" is the rare end-of-journey close
                    // (STARTED → CLOSED, can't be undone) and is intentionally
                    // de-emphasised to a quiet text link with its warning note, so
                    // it doesn't compete with the everyday Done action.
                    <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                        <LoadingButton
                            data-testid="hub-done-btn"
                            variant="contained"
                            loading={isLoader}
                            onClick={handleClose}
                            disabled={isLoader}
                            sx={{
                                bgcolor: '#00AB55',
                                color: '#ffffff',
                                fontSize: '14px',
                                fontWeight: 700,
                                letterSpacing: '-0.01em',
                                textTransform: 'none',
                                borderRadius: '8px',
                                boxShadow: 'none',
                                px: 4,
                                py: 1,
                                minWidth: '140px',
                                '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                                '&:active': { bgcolor: '#017A3D' },
                                '&.Mui-disabled': { bgcolor: 'rgba(0,171,85,0.35)', color: '#ffffff' },
                            }}
                        >
                            Done
                        </LoadingButton>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <LoadingButton
                                data-testid="hub-complete-treatment-btn"
                                variant="text"
                                loading={isLoader}
                                onClick={handleYes}
                                disabled={isLoader}
                                sx={{
                                    color: '#86868b',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    letterSpacing: '-0.005em',
                                    textTransform: 'none',
                                    borderRadius: '6px',
                                    boxShadow: 'none',
                                    px: 1.25,
                                    py: 0.5,
                                    minWidth: 0,
                                    '&:hover': { bgcolor: 'rgba(209,67,67,0.06)', color: '#D14343' },
                                    '&.Mui-disabled': { color: 'rgba(134,134,139,0.5)' },
                                }}
                            >
                                Complete treatment
                            </LoadingButton>
                            <Typography sx={{ fontSize: '10px', color: '#aeaeb2', letterSpacing: '-0.005em', lineHeight: 1.3 }}>
                                Ends &amp; closes the treatment · can&apos;t be undone
                            </Typography>
                        </Box>
                    </Container>
                ) : (
                    // Legacy footer (no row) — unchanged Cancel + Finish.
                    <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="text"
                            onClick={handleClose}
                            disabled={isLoader}
                            sx={{
                                color: '#1d1d1f',
                                fontSize: '13px',
                                fontWeight: 500,
                                letterSpacing: '-0.01em',
                                textTransform: 'none',
                                px: 2,
                                py: 0.75,
                                borderRadius: '6px',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                            }}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            type="submit"
                            loading={isLoader}
                            loadingPosition="start"
                            onClick={handleYes}
                            disabled={isLoader}
                            sx={{
                                bgcolor: '#00AB55',
                                color: '#ffffff',
                                fontSize: '13px',
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                                textTransform: 'none',
                                borderRadius: '6px',
                                boxShadow: 'none',
                                px: 2.25,
                                py: 0.75,
                                minWidth: '88px',
                                '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                                '&:active': { bgcolor: '#017A3D' },
                                '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                            }}
                        >
                            Finish
                        </LoadingButton>
                    </Container>
                )}
            </Box>
        </Dialog>

        {/* Pause dialog — checklist-wide pause (the /pause/ endpoint pauses the
            whole task-group). Date picker for paused_until. */}
        <Dialog
            data-testid="hub-pause-dialog"
            open={pauseDialogItem != null}
            onClose={() => setPauseDialogItem(null)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px' } }}
        >
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73', mb: 0.5 }}>
                    Pause checklist
                </Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    Pause this checklist
                </Typography>
            </Box>
            <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                <Typography sx={{ fontSize: '13px', color: '#3c3c43', mb: 1.5, letterSpacing: '-0.005em' }}>
                    Pauses the whole checklist until the chosen date. Resume on:
                </Typography>
                <input
                    data-testid="hub-pause-date"
                    type="datetime-local"
                    value={pauseUntilDate}
                    onChange={(e) => setPauseUntilDate(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        borderRadius: '8px',
                        border: '1.5px solid rgba(0,0,0,0.15)',
                        fontFamily: 'inherit',
                        color: '#1d1d1f',
                        outline: 'none',
                        boxSizing: 'border-box',
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <Button
                    onClick={() => setPauseDialogItem(null)}
                    sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                >
                    Cancel
                </Button>
                <Button
                    data-testid="hub-pause-confirm"
                    variant="contained"
                    disableElevation
                    disabled={!pauseUntilDate}
                    onClick={handlePauseConfirm}
                    sx={{
                        bgcolor: '#078DEE',
                        color: '#fff',
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 600,
                        py: 0.75,
                        px: 2.5,
                        borderRadius: '8px',
                        '&:hover': { bgcolor: '#0670c4' },
                    }}
                >
                    Pause checklist
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

CompletedTreatmentHub.propTypes = {
    isLoader: PropTypes.bool,
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    handleYes: PropTypes.func,
    row: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CompletedTreatmentHub.defaultProps = {
    isLoader: false,
    open: false,
    handleClose: undefined,
    handleYes: undefined,
    row: undefined,
    practiceId: undefined,
};

export default CompletedTreatmentHub;
